import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { SelectInput, RadioInput, CurrencyInput } from "components/Input/Inputs";
import { IFormInputMap } from '../hooks/useDealForm';
import { SELELCT, RADIO, CURRENCY, NUMBER } from "common/constants";
import SettlementRateInput from './SettlementRateInput';

interface FormFieldRendererProps {
  data: any;
  index: number;
  isDisabled: boolean;
  showOptionalFields: boolean;
  isCalcLoading: boolean;
  limitCustomerRate: number;
  limitCustomerRate2: number;
  servicePartnerId: string | number;
  partnerStatus: string;
  setActiveField: (field: number | ((prev: number) => number)) => void;
  shouldDisplayField: (fieldName: string) => boolean;
  formDataLength: number;
}

const FormFieldRenderer: React.FC<FormFieldRendererProps> = ({
  data,
  index,
  isDisabled,
  showOptionalFields,
  isCalcLoading,
  limitCustomerRate,
  limitCustomerRate2,
  servicePartnerId,
  partnerStatus,
  setActiveField,
  shouldDisplayField,
  formDataLength,
}) => {
  const form = useFormContext<IFormInputMap>();

//   if (isDisabled) return null;
  if (data.optional && !data.alwaysShow && !showOptionalFields) return null;

  if (
    ["dob", "pan", "remitter_name"].includes(data.name) &&
    servicePartnerId !== "17"
  ) {
    return null;
  }

  if (!shouldDisplayField(data.name)) {
    return null;
  }

  const renderField = (field: any) => {
    switch (data.type) {
      case SELELCT:
      case "select":
        return (
          <SelectInput<IFormInputMap>
            name={data.name}
            isDisabled={isDisabled}
            field={field}
            options={data.options}
            placeholder={data.placeholder}
          />
        );

      case RADIO:
      case "radio":
        return (
          <RadioInput
            defaultValue="BEN"
            options={data.options ?? []}
            field={{
              ...field,
              value: field.value || "BEN",
            }}
          />
        );

      case CURRENCY:
      case "currency":
        return (
          <CurrencyInput
            currencyType={data.currency as string}
            currencySymbol={data.currencySymbol ?? ""}
            field={{
              ...field,
              disabled: data.name === "rate" && partnerStatus === "pending",
              onChange: (e) => {
                field.onChange(e);
                // if (field.name === "rate") {
                //   const value = parseFloat(e.target.value);
                //   if (value >= limitCustomerRate && value >= limitCustomerRate2) {
                //     field.onChange("");                   
                //   }
                // }
              },
            }}
            isLoading={isCalcLoading}
          />
        );

      case NUMBER:
      case "number":
        return (
          <Input
            type="number"
            {...field}
            className="m-0 h-12 max-w-[340px] border border-gray-border-gray-lite p-4 rounded-lg py-2 px-3"
          />
        );

      case "settlement-rate-input":
        return localStorage.getItem("level") === "1" ? (
          <SettlementRateInput field={field} />
        ) : null;

      case "date":
        return (
          <Input
            type="date"
            {...field}
            className="m-0 h-12 max-w-[340px] border border-gray-border-gray-lite p-4 rounded-lg py-2 px-3"
          />
        );

      case "text":
      default:
        return (
          <Input
            type="text"
            {...field}
            placeholder={data.placeholder}
            className="m-0 h-12 max-w-[340px] border border-gray-border-gray-lite p-4 rounded-lg py-2 px-3"
          />
        );
    }
  };

  return (
    <div key={data.name} className="flex justify-between max-w-[340px]">
      <div
        className={`flex items-center flex-col after:right-10 ${
          index === formDataLength - 1 && "after:border-none"
        } ${
          isDisabled
            ? "after:h-14 "
            : "after:h-28 after:border-blue-700"
        } absolute after:relative after:content-[''] after:border after:border-dashed`}
      >
        <img
          className="relative -left-10"
          src={`${
            isDisabled
              ? "images/overview/ellipse-gray.svg"
              : "images/overview/ellipse.svg"
          }`}
          alt=""
        />
      </div>
      <Controller
        name={data.name as keyof IFormInputMap}
        control={form.control}
        defaultValue=""
        render={({ field }) => (
          <FormItem className="space-y-0 w-full flex flex-col gap-3">
            <FormLabel
              className={`${
                isDisabled ? "text-diabled" : "text-black-label"
              } text-sm font-medium leading-[17px]`}
            >
              {data.label}
            </FormLabel>
            {renderField(field)}
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
};

export default FormFieldRenderer;